import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { DealNonTariffSection, type CandidateLite } from '../components/DealNonTariffSection';
import { DealDtDatasetSection } from '../components/DealDtDatasetSection';

interface Deal {
  id: string;
  dealNumber: string;
  status: string;
  importerName: string | null;
  exporterName: string | null;
  exporterCountry: string | null;
  hsCode: string | null;
  description: string | null;
  valueAmount: string | null;
  valueCurrency: string | null;
  createdAt: string;
}

interface DealUpload {
  document_id: string;
  filename: string;
  mime: string | null;
  sha256: string | null;
  bytes: number | null;
  status: string;
  uploaded_at: string;
}

interface Top5Item {
  code: string;
  prob: number | null;
  rank: number | null;
  name: string | null;
  rate: { display: string | null; ad_valorem_pct: number | null };
}

interface Candidate {
  id: string;
  document_id: string;
  filename: string | null;
  row_idx: number;
  supplier_sku: string | null;
  name: string | null;            // CN/original from file
  name_ru: string | null;         // machine translation (null until LLM)
  material: string | null;
  description: string | null;
  qty: number | null;             // Кол-во мест
  qty_pieces: number | null;      // Штук (小件数)
  weight_kg: number | null;
  volume_m3: number | null;
  meters: number | null;
  density_calc: number | null;    // kg/m³ — расчётная
  density_raw: number | null;     // из файла (для сверки)
  category: string | null;
  destination: string | null;
  business_code: string | null;
  prepayment: string | null;
  extra_fields: Record<string, unknown>;
  top5: Top5Item[];               // все кандидаты со ставками
  tnved_code: string | null;
  tnved_score: number | null;
  model_version: string | null;
  status: string;                 // proposed | approved | overridden | rejected
  approved_code: string | null;
  signals_used: string[];
  has_image: boolean;
  image_url: string | null;       // presigned S3 URL (5 min)
}

interface DutyRow {
  id: string;
  candidate_id: string;
  hs_code: string;
  import_country: string;
  customs_value: string;
  currency: string;
  customs_value_rub: string;
  duty_rub: string;
  excise_rub: string;
  vat_rate: string;
  vat_rub: string;
  customs_fee_rub: string;
  total_rub: string;
  rate_source: string | null;
  vat_source: string | null;
  calculated_at: string;
}

interface DutyAggregate {
  customs_value_rub: number;
  duty_rub: number;
  excise_rub: number;
  vat_rub: number;
  customs_fee_rub: number;
  total_rub: number;
}

interface OptVariant {
  hsCode: string;
  rank: number | null;
  prob: number | null;
  cost: number | null;
  plausible: boolean;
  error?: string;
}

interface OptRecommendation {
  candidate_id: string;
  product_name: string | null;
  current_code: string | null;
  current_cost: number | null;
  recommended_code: string | null;
  recommended_cost: number | null;
  delta_rub: number;
  variants: OptVariant[];
  warnings: string[];
}

interface OptimizationRun {
  id: string;
  total_naive_rub: string | number;
  total_optimal_rub: string | number;
  savings_rub: string | number;
  savings_ratio: string | number;
  recommendations: OptRecommendation[];
  ran_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  classified: 'bg-blue-100 text-blue-700',
  calculated: 'bg-purple-100 text-purple-700',
  docs_ready: 'bg-emerald-100 text-emerald-700',
  submitted: 'bg-yellow-100 text-yellow-700',
  cleared: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  archived: 'bg-gray-100 text-gray-500',
};

function fmtBytes(n: number | null): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('ru-RU');
  } catch {
    return '—';
  }
}

export function DealsPage(): JSX.Element {
  const { user, token, logout } = useAuth();
  const [deals, setDeals] = useState<Deal[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // RequireAuth ensures user & token present
  if (!user || !token) return <></>;

  useEffect(() => {
    void (async () => {
      try {
        const r = await fetch('/api/v1/deals?limit=100', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.status === 401) {
          logout();
          return;
        }
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }
        const data = (await r.json()) as { items: Deal[] };
        setDeals(data.items ?? []);
      } catch (e) {
        setErr((e as Error).message);
      }
    })();
  }, [token, logout]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-ntr-primary">Мои сделки</h2>
        <Link
          to="/upload"
          className="rounded-md bg-ntr-primary px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-ntr-primary/90"
        >
          + Загрузить файлы
        </Link>
      </div>

      {err && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
      )}

      {deals === null && !err && (
        <p className="text-sm text-ntr-muted">Загрузка…</p>
      )}

      {deals && deals.length === 0 && (
        <p className="rounded-md bg-white p-6 text-sm text-ntr-muted shadow-sm ring-1 ring-ntr-primary/10">
          Пока сделок нет. <Link to="/upload" className="text-ntr-primary hover:underline">Загрузить первый файл →</Link>
        </p>
      )}

      {deals && deals.length > 0 && (
        <div className="overflow-x-auto rounded-lg bg-white shadow-sm ring-1 ring-ntr-primary/10">
          <table className="min-w-full divide-y divide-ntr-primary/10">
            <thead className="bg-ntr-primary/5">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-ntr-primary">№ сделки</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-ntr-primary">Статус</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-ntr-primary">Описание</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-ntr-primary">HS</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-ntr-primary">Стоимость</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-ntr-primary">Создана</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ntr-primary/10">
              {deals.map((d) => (
                <tr key={d.id} className="hover:bg-ntr-primary/5">
                  <td className="px-4 py-2 text-sm">
                    <Link
                      to={`/deals/${d.id}`}
                      className="font-medium text-ntr-primary hover:underline"
                    >
                      {d.dealNumber}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-sm">
                    <span
                      className={
                        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ' +
                        (STATUS_COLORS[d.status] ?? 'bg-gray-100 text-gray-700')
                      }
                    >
                      {d.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-ntr-muted">
                    {d.description ? d.description.slice(0, 80) : '—'}
                  </td>
                  <td className="px-4 py-2 text-sm font-mono">{d.hsCode ?? '—'}</td>
                  <td className="px-4 py-2 text-sm">
                    {d.valueAmount
                      ? `${d.valueAmount} ${d.valueCurrency ?? ''}`.trim()
                      : '—'}
                  </td>
                  <td className="px-4 py-2 text-xs text-ntr-muted">{fmtDate(d.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function DealDetailPage(): JSX.Element {
  const { user, token, logout } = useAuth();
  const { dealId } = useParams<{ dealId: string }>();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [docs, setDocs] = useState<DealUpload[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [classifyBusy, setClassifyBusy] = useState(false);
  const [classifyMsg, setClassifyMsg] = useState<string | null>(null);
  const [translateBusy, setTranslateBusy] = useState(false);
  const [enrichBusy, setEnrichBusy] = useState(false);
  const [dutyRows, setDutyRows] = useState<DutyRow[]>([]);
  const [dutyAgg, setDutyAgg] = useState<DutyAggregate | null>(null);
  const [dutyBusy, setDutyBusy] = useState(false);
  const [dutyMsg, setDutyMsg] = useState<string | null>(null);
  const [customsValues, setCustomsValues] = useState<Record<string, string>>({});
  const [importCountry, setImportCountry] = useState<'RUS' | 'BLR' | 'KAZ' | 'KGZ' | 'ARM'>('RUS');
  const [optRun, setOptRun] = useState<OptimizationRun | null>(null);
  const [optBusy, setOptBusy] = useState(false);
  const [optMsg, setOptMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  // RequireAuth ensures user & token present
  if (!user || !token) return <></>;

  useEffect(() => {
    void (async () => {
      if (!dealId) return;
      try {
        const [rDeal, rDocs, rCands, rDuty] = await Promise.all([
          fetch(`/api/v1/deals/${dealId}`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`/api/v1/deals/${dealId}/uploads`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`/api/v1/deals/${dealId}/candidates`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`/api/v1/deals/${dealId}/duty`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (rDeal.status === 401) {
          logout();
          return;
        }
        if (!rDeal.ok) throw new Error(`Deal HTTP ${rDeal.status}`);
        setDeal((await rDeal.json()) as Deal);

        if (rDocs.ok) {
          const data = (await rDocs.json()) as { uploads: DealUpload[] };
          setDocs(data.uploads ?? []);
        }
        if (rCands.ok) {
          const data = (await rCands.json()) as { candidates: Candidate[] };
          setCandidates(data.candidates ?? []);
        }
        if (rDuty.ok) {
          const data = (await rDuty.json()) as { rows: DutyRow[]; aggregate: DutyAggregate | null };
          setDutyRows(data.rows ?? []);
          setDutyAgg(data.aggregate);
        }
        const rOpt = await fetch(`/api/v1/deals/${dealId}/optimization`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (rOpt.ok) {
          const d = (await rOpt.json()) as { run: OptimizationRun | null };
          setOptRun(d.run);
        }
      } catch (e) {
        setErr((e as Error).message);
      }
    })();
  }, [dealId, token, logout, reloadTick]);

  // ── AUTO-POLL: пока есть строки в процессе (нет tnved_code или не верифицированы LLM),
  // опрашиваем /candidates каждые 3с. Стоп когда все строки имеют 'llm_verify' в signals_used
  // или status не 'proposed' (брокер уже решил) — это значит пиплайн закончился.
  useEffect(() => {
    if (!dealId || candidates.length === 0) return;
    const inProgress = candidates.some(
      (c) =>
        c.status === 'proposed' &&
        !(c.signals_used ?? []).includes('llm_verify'),
    );
    if (!inProgress) return;
    const t = setInterval(() => {
      setReloadTick((x) => x + 1);
    }, 3000);
    return () => clearInterval(t);
  }, [dealId, candidates]);

  const runDecision = async (candId: string, kind: 'approve' | 'reject' | 'override', body: { final_code?: string; reason?: string }): Promise<void> => {
    try {
      const r = await fetch(`/api/v1/candidates/${candId}/${kind}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const j = (await r.json()) as { error?: string };
      if (!r.ok) {
        setErr(j.error ?? `HTTP ${r.status}`);
        return;
      }
      setReloadTick((t) => t + 1);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const runOptimize = async (): Promise<void> => {
    if (!dealId) return;
    const eligible = candidates.filter((c) => c.status === 'approved' || c.status === 'overridden');
    if (eligible.length === 0) {
      setOptMsg('Нет утверждённых кодов');
      return;
    }
    const rows = eligible
      .map((c) => {
        const v = customsValues[c.id];
        const num = v ? Number(v.replace(',', '.')) : 0;
        if (!num || !Number.isFinite(num) || num <= 0) return null;
        return { candidate_id: c.id, customs_value: num, currency: 'USD' };
      })
      .filter((r): r is { candidate_id: string; customs_value: number; currency: string } => r !== null);
    if (rows.length === 0) {
      setOptMsg('Укажите таможенную стоимость в блоке расчёта выше');
      return;
    }
    setOptBusy(true); setOptMsg(null);
    try {
      const r = await fetch(`/api/v1/deals/${dealId}/optimize`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ import_country: importCountry, rows }),
      });
      const j = (await r.json()) as OptimizationRun & { error?: string; savings_rub?: number };
      if (!r.ok) {
        setOptMsg(j.error ?? `HTTP ${r.status}`);
      } else {
        setOptMsg(`Экономия: ${Math.round(Number(j.savings_rub ?? 0)).toLocaleString('ru-RU')} RUB`);
        setReloadTick((t) => t + 1);
      }
    } catch (e) {
      setOptMsg((e as Error).message);
    } finally {
      setOptBusy(false);
    }
  };

  const runCalculateDuty = async (): Promise<void> => {
    if (!dealId) return;
    const eligible = candidates.filter((c) => c.status === 'approved' || c.status === 'overridden');
    if (eligible.length === 0) {
      setDutyMsg('Нет утверждённых кодов для расчёта');
      return;
    }
    const rows = eligible
      .map((c) => {
        const v = customsValues[c.id];
        const num = v ? Number(v.replace(',', '.')) : 0;
        if (!num || !Number.isFinite(num) || num <= 0) return null;
        return { candidate_id: c.id, customs_value: num, currency: 'USD' };
      })
      .filter((r): r is { candidate_id: string; customs_value: number; currency: string } => r !== null);
    if (rows.length === 0) {
      setDutyMsg('Укажите таможенную стоимость (USD) хотя бы для одной строки');
      return;
    }
    setDutyBusy(true);
    setDutyMsg(null);
    try {
      const r = await fetch(`/api/v1/deals/${dealId}/calculate-duty`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ import_country: importCountry, rows }),
      });
      const j = (await r.json()) as {
        calculated?: number;
        failed?: number;
        errors?: Array<{ candidate_id: string; error: string }>;
        error?: string;
        message?: string;
      };
      if (!r.ok) {
        setDutyMsg(j.error ?? j.message ?? `HTTP ${r.status}`);
        return;
      }
      const errs = j.errors ?? [];
      setDutyMsg(
        `Рассчитано: ${j.calculated ?? 0}` +
          (errs.length
            ? ` · Ошибок: ${errs.length} (${errs.slice(0, 2).map((e) => e.error.slice(0, 60)).join(' | ')})`
            : ''),
      );
      setReloadTick((t) => t + 1);
    } catch (e) {
      setDutyMsg((e as Error).message);
    } finally {
      setDutyBusy(false);
    }
  };

  // Accept a specific code from the top-5 (uses override with the exact code so
  // it ends up in approved_code regardless of which BERT/CN rank it had).
  const onAcceptVariant = (c: Candidate, code: string): Promise<void> => {
    if (code === c.tnved_code) {
      // top-1 case — just approve, BERT top-1 stays.
      return runDecision(c.id, 'approve', {});
    }
    return runDecision(c.id, 'override', { final_code: code });
  };
  // Broker enters a fully custom code (not in top-5).
  const onCustomCode = (c: Candidate): Promise<void> => {
    const code = window.prompt(
      `Введите ваш код ТН ВЭД (6–10 цифр) для «${c.name ?? ''}»:`,
      c.approved_code ?? c.tnved_code ?? '',
    );
    if (!code) return Promise.resolve();
    if (!/^\d{6,10}$/.test(code)) {
      setErr('Некорректный код: нужны 6–10 цифр');
      return Promise.resolve();
    }
    const reason = window.prompt('Обоснование (опционально):', '') ?? undefined;
    return runDecision(c.id, 'override', reason ? { final_code: code, reason } : { final_code: code });
  };
  // Bulk action: auto-approve every still-proposed row with its BERT top-1 code,
  // then jump straight into the duty-calc + optimize section.
  const onAcceptAllAndOptimize = async (): Promise<void> => {
    const stillProposed = candidates.filter((c) => c.status === 'proposed' && c.tnved_code);
    if (stillProposed.length === 0) {
      setReloadTick((t) => t + 1);
      return;
    }
    setClassifyBusy(true);
    setClassifyMsg(`Принимаю ${stillProposed.length} строк…`);
    try {
      for (const c of stillProposed) {
        await fetch(`/api/v1/candidates/${c.id}/approve`, {
          method: 'POST',
          headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
          body: '{}',
        });
      }
      setClassifyMsg(`Принято ${stillProposed.length}, перехожу к расчёту`);
      setReloadTick((t) => t + 1);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setClassifyBusy(false);
    }
  };

  const runClassify = async (): Promise<void> => {
    if (!dealId) return;
    setClassifyBusy(true);
    setClassifyMsg(null);
    try {
      const r = await fetch(`/api/v1/deals/${dealId}/classify-uploads`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = (await r.json()) as { candidates_created?: number; message?: string; error?: string };
      if (!r.ok) {
        setClassifyMsg(j.message ?? j.error ?? `HTTP ${r.status}`);
      } else {
        setClassifyMsg(`Создано кандидатов: ${j.candidates_created ?? 0}`);
        setReloadTick((t) => t + 1);
      }
    } catch (e) {
      setClassifyMsg((e as Error).message);
    } finally {
      setClassifyBusy(false);
    }
  };

  // L2 каскад v2: enrich + classify в одном вызове.
  // LLM извлекает структуру из CN, BERT v2 классифицирует на расширенном RU тексте,
  // LLM verify выбирает один из top-5. Результат кэшируется.
  const runEnrichAndClassify = async (): Promise<void> => {
    if (!dealId) return;
    setEnrichBusy(true);
    setClassifyMsg('Распознаю товары и классифицирую… (может занять 30-60 сек)');
    try {
      const r = await fetch(`/api/v1/deals/${dealId}/enrich-and-classify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = (await r.json()) as {
        total?: number;
        enriched?: number;
        from_cache?: number;
        from_llm?: number;
        verified?: number;
        verify_errors?: number;
        updated?: number;
        message?: string;
        error?: string;
      };
      if (!r.ok) {
        setClassifyMsg(j.message ?? j.error ?? `HTTP ${r.status}`);
      } else {
        setClassifyMsg(
          `Готово. Обработано: ${j.updated ?? 0}/${j.total ?? 0} ` +
            `(кэш: ${j.from_cache ?? 0}, LLM extract: ${j.from_llm ?? 0}, ` +
            `LLM verify: ${j.verified ?? 0}, ошибок: ${j.verify_errors ?? 0})`,
        );
        setReloadTick((t) => t + 1);
      }
    } catch (e) {
      setClassifyMsg((e as Error).message);
    } finally {
      setEnrichBusy(false);
    }
  };

  // CN→RU перевод исходных товарных названий через LLM + кэш.
  const runTranslate = async (): Promise<void> => {
    if (!dealId) return;
    setTranslateBusy(true);
    setClassifyMsg(null);
    try {
      const r = await fetch(`/api/v1/deals/${dealId}/translate-names`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = (await r.json()) as {
        total?: number;
        from_cache?: number;
        from_llm?: number;
        updated?: number;
        message?: string;
        error?: string;
      };
      if (!r.ok) {
        setClassifyMsg(j.message ?? j.error ?? `HTTP ${r.status}`);
      } else {
        setClassifyMsg(
          j.message ??
            `Переведено: ${j.updated ?? 0} (кэш: ${j.from_cache ?? 0}, LLM: ${j.from_llm ?? 0})`,
        );
        setReloadTick((t) => t + 1);
      }
    } catch (e) {
      setClassifyMsg((e as Error).message);
    } finally {
      setTranslateBusy(false);
    }
  };

  const download = async (docId: string): Promise<void> => {
    const r = await fetch(`/api/v1/uploads/${docId}/presigned`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) {
      const data = (await r.json()) as { url: string };
      window.open(data.url, '_blank');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/deals" className="text-sm text-ntr-primary hover:underline">
          ← Все сделки
        </Link>
      </div>
      {err && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {!deal && !err && <p className="text-sm text-ntr-muted">Загрузка…</p>}
      {deal && (
        <>
          <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-ntr-primary/10">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-ntr-primary">{deal.dealNumber}</h2>
                <p className="mt-1 text-sm text-ntr-muted">
                  Создана {fmtDate(deal.createdAt)}
                </p>
              </div>
              <span
                className={
                  'inline-flex rounded-full px-3 py-1 text-sm font-medium ' +
                  (STATUS_COLORS[deal.status] ?? 'bg-gray-100 text-gray-700')
                }
              >
                {deal.status}
              </span>
            </div>
            <dl className="mt-6 grid grid-cols-1 gap-x-4 gap-y-3 text-sm md:grid-cols-2">
              <div>
                <dt className="text-xs text-ntr-muted">Импортёр</dt>
                <dd className="text-ntr-text">{deal.importerName ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-ntr-muted">Экспортёр</dt>
                <dd className="text-ntr-text">
                  {deal.exporterName ?? '—'}
                  {deal.exporterCountry ? ` (${deal.exporterCountry})` : ''}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-ntr-muted">Описание</dt>
                <dd className="text-ntr-text">{deal.description ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-ntr-muted">HS-код</dt>
                <dd className="font-mono text-ntr-text">{deal.hsCode ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-ntr-muted">Стоимость</dt>
                <dd className="text-ntr-text">
                  {deal.valueAmount
                    ? `${deal.valueAmount} ${deal.valueCurrency ?? ''}`.trim()
                    : '—'}
                </dd>
              </div>
            </dl>
          </div>

          <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-ntr-primary/10">
            <h3 className="text-lg font-semibold text-ntr-primary">
              Документы ({docs.length})
            </h3>
            {docs.length === 0 ? (
              <p className="mt-3 text-sm text-ntr-muted">
                Нет загруженных документов.{' '}
                <Link to="/upload" className="text-ntr-primary hover:underline">
                  Загрузить →
                </Link>
              </p>
            ) : (
              <ul className="mt-4 divide-y divide-ntr-primary/10">
                {docs.map((d) => (
                  <li key={d.document_id} className="flex items-center justify-between py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ntr-text">
                        {d.filename}
                      </p>
                      <p className="text-xs text-ntr-muted">
                        {fmtBytes(d.bytes)} · sha256:{' '}
                        <code>{d.sha256?.slice(0, 16) ?? '—'}…</code> · {d.status} · {fmtDate(d.uploaded_at)}
                      </p>
                    </div>
                    <button
                      onClick={() => void download(d.document_id)}
                      className="ml-3 text-sm text-ntr-primary hover:underline"
                    >
                      Скачать
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-ntr-primary/10">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ntr-primary">
                Классификация ТН ВЭД ({candidates.length})
                {(() => {
                  if (candidates.length === 0) return null;
                  const done = candidates.filter((c) => (c.signals_used ?? []).includes('llm_verify') || c.status !== 'proposed').length;
                  if (done === candidates.length) return null;
                  return (
                    <span className="ml-3 inline-flex items-center gap-1 rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700">
                      <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-purple-600" />
                      Распознаю и классифицирую… {done}/{candidates.length}
                    </span>
                  );
                })()}
              </h3>
              <div className="flex items-center gap-2">
                {/* Основной поток — автоматический: загрузил файл → видишь результат. Кнопка «Перезапустить» — в случае если нужно переходить. */}
                {(candidates.length === 0 && docs.length > 0) && (
                  <button
                    onClick={() => void runClassify()}
                    disabled={classifyBusy}
                    className="rounded-md bg-ntr-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-ntr-primary/90 disabled:opacity-50"
                  >
                    {classifyBusy ? 'Запускаю…' : '⚡ Запустить классификацию'}
                  </button>
                )}
                {candidates.length > 0 && (
                  <button
                    onClick={() => void runEnrichAndClassify()}
                    disabled={enrichBusy || translateBusy || classifyBusy}
                    title="Перезапустить распознавание и классификацию (LLM extract + BERT + LLM verify)"
                    className="rounded-md border border-ntr-primary/30 bg-white px-3 py-1.5 text-xs font-medium text-ntr-muted shadow-sm hover:bg-ntr-primary/5 disabled:opacity-50"
                  >
                    {enrichBusy ? '…' : '↻ Перезапустить'}
                  </button>
                )}
              </div>
            </div>
            {classifyMsg && (
              <p className="mt-2 text-sm text-ntr-muted">{classifyMsg}</p>
            )}
            {candidates.length === 0 ? (
              <p className="mt-3 text-sm text-ntr-muted">
                {docs.length === 0
                  ? 'Сначала загрузите файлы во вкладке «Загрузить».'
                  : 'Нажмите «Классифицировать» — BERT v2 предложит коды ТН ВЭД для каждой строки.'}
              </p>
            ) : (
              <div className="mt-4 max-h-[70vh] overflow-auto rounded border border-ntr-primary/10">
                <table className="min-w-full divide-y divide-ntr-primary/10 text-sm">
                  <thead className="sticky top-0 z-20">
                    <tr className="text-left text-[11px] uppercase text-ntr-muted">
                      <th className="sticky top-0 z-20 bg-ntr-primary/10 px-2 py-2">#</th>
                      <th className="sticky top-0 z-20 bg-ntr-primary/10 px-2 py-2">Фото</th>
                      <th className="sticky top-0 z-20 bg-ntr-primary/10 px-2 py-2">Артикул</th>
                      <th className="sticky top-0 z-20 bg-ntr-primary/10 px-2 py-2">Товар (CN)</th>
                      <th className="sticky top-0 z-20 bg-ntr-primary/10 px-2 py-2">Перевод</th>
                      <th className="sticky top-0 z-20 bg-ntr-primary/10 px-2 py-2">Описание</th>
                      <th className="sticky top-0 z-20 bg-ntr-primary/10 px-2 py-2 text-right">Кол-во</th>
                      <th className="sticky top-0 z-20 bg-ntr-primary/10 px-2 py-2 text-right">Шт</th>
                      <th className="sticky top-0 z-20 bg-ntr-primary/10 px-2 py-2 text-right">Вес, кг</th>
                      <th className="sticky top-0 z-20 bg-ntr-primary/10 px-2 py-2 text-right">Куб, м³</th>
                      <th className="sticky top-0 z-20 bg-ntr-primary/10 px-2 py-2 text-right">Плотн.</th>
                      <th className="sticky top-0 z-20 bg-ntr-primary/10 px-2 py-2">ТН ВЭД — top-5 + ставка</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ntr-primary/10">
                    {candidates.map((c) => {
                      const finalCode = c.approved_code ?? (c.status === 'approved' ? c.tnved_code : null);
                      const top5 = (c.top5 && c.top5.length > 0
                        ? c.top5
                        : c.tnved_code
                          ? [{ code: c.tnved_code, prob: c.tnved_score, rank: 1, name: null, rate: { display: null, ad_valorem_pct: null } }]
                          : []) as Top5Item[];
                      return (
                        <tr key={c.id} className="align-top hover:bg-ntr-primary/5">
                          <td className="px-2 py-3 text-xs text-ntr-muted">{c.row_idx + 1}</td>
                          <td className="px-2 py-3">
                            {c.image_url ? (
                              <a href={c.image_url} target="_blank" rel="noreferrer">
                                <img
                                  src={c.image_url}
                                  alt={c.name ?? ''}
                                  className="h-16 w-16 rounded border border-ntr-primary/10 object-cover"
                                />
                              </a>
                            ) : c.has_image ? (
                              <div className="flex h-16 w-16 items-center justify-center rounded border border-dashed border-ntr-primary/20 text-[10px] text-ntr-muted">⏳ фото</div>
                            ) : (
                              <div className="flex h-16 w-16 items-center justify-center rounded border border-dashed border-ntr-primary/10 text-[10px] text-ntr-muted">—</div>
                            )}
                          </td>
                          <td className="px-2 py-3 font-mono text-xs text-ntr-text">{c.supplier_sku ?? '—'}</td>
                          <td className="px-2 py-3 font-medium text-ntr-text">{c.name ?? '—'}</td>
                          <td className="px-2 py-3 text-xs text-ntr-muted">
                            {c.name_ru ?? <span className="italic text-ntr-muted/60">перевод не подключён</span>}
                          </td>
                          <td className="px-2 py-3 text-xs text-ntr-muted">{c.description ?? '—'}</td>
                          <td className="px-2 py-3 text-right text-xs">{c.qty ?? '—'}</td>
                          <td className="px-2 py-3 text-right text-xs">{c.qty_pieces ?? '—'}</td>
                          <td className="px-2 py-3 text-right text-xs">{c.weight_kg ?? '—'}</td>
                          <td className="px-2 py-3 text-right text-xs">{c.volume_m3 ?? '—'}</td>
                          <td className="px-2 py-3 text-right text-xs">
                            {c.density_calc != null ? c.density_calc.toFixed(1) : '—'}
                          </td>
                          <td className="px-2 py-3">
                            {top5.length === 0 ? (
                              <div className="text-xs text-ntr-muted">
                                Код не определён — введите вручную.
                                <button
                                  onClick={() => void onCustomCode(c)}
                                  className="ml-2 rounded bg-amber-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-amber-700"
                                >
                                  ✎ Ввести код
                                </button>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                {top5.map((t) => {
                                  const isApproved = finalCode === t.code;
                                  // Сокращаем «EUR/pair» → «EUR/пара», «kg» → «кг» и т.п. для экономии места
                                  const rateShort = t.rate?.display
                                    ? t.rate.display
                                        .replace(/EUR\/pair/g, 'EUR/пара')
                                        .replace(/EUR\/kg/g, 'EUR/кг')
                                        .replace(/EUR\/m\^?3/g, 'EUR/м³')
                                        .replace(/EUR\/m2/g, 'EUR/м²')
                                        .replace(/EUR\/l/g, 'EUR/л')
                                        .replace(/EUR\/p\b/g, 'EUR/шт')
                                        .replace(/EUR\/piece/g, 'EUR/шт')
                                        .replace(/USD\/kg/g, 'USD/кг')
                                        .replace(/\s*но не менее\s+/gi, ' ≥ ')
                                        .trim()
                                    : null;
                                  return (
                                    <div
                                      key={t.code}
                                      className={
                                        'grid items-center gap-3 rounded px-2 py-1 ' +
                                        'grid-cols-[130px_42px_minmax(140px,180px)_minmax(0,1fr)_84px] ' +
                                        (isApproved ? 'bg-green-50 ring-1 ring-green-300' : 'bg-white')
                                      }
                                    >
                                      <span className="truncate font-mono text-sm font-semibold" title={t.code}>
                                        {t.code}
                                      </span>
                                      <span className="truncate text-right text-[10px] text-ntr-muted">
                                        {t.prob != null ? `${(t.prob * 100).toFixed(0)}%` : ''}
                                      </span>
                                      <span
                                        className={
                                          'inline-flex min-w-0 items-center justify-start truncate whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ' +
                                          (t.rate?.display ? 'bg-blue-50 text-blue-700' : 'text-ntr-muted')
                                        }
                                        title={t.rate?.display ?? 'Ставка не найдена'}
                                      >
                                        {rateShort ?? '—'}
                                      </span>
                                      <span className="min-w-0 truncate text-[11px] text-ntr-muted" title={t.name ?? ''}>
                                        {t.name ?? '—'}
                                      </span>
                                      {c.status === 'proposed' ? (
                                        <button
                                          onClick={() => void onAcceptVariant(c, t.code)}
                                          className="rounded bg-green-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-green-700"
                                        >
                                          ✓ Принять
                                        </button>
                                      ) : isApproved ? (
                                        <span className="text-[11px] font-medium text-green-700">✓ принят</span>
                                      ) : (
                                        <button
                                          onClick={() => void onAcceptVariant(c, t.code)}
                                          className="rounded bg-amber-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-amber-700"
                                        >
                                          Сменить
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                                <button
                                  onClick={() => void onCustomCode(c)}
                                  className="mt-1 w-full rounded border border-dashed border-amber-400 px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-50"
                                >
                                  ✎ Другой код (вручную)
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-xs text-ntr-muted">
                    Модель: {candidates[0]?.model_version ?? '—'}. Принято{' '}
                    {candidates.filter((c) => c.status === 'approved' || c.status === 'overridden').length}/
                    {candidates.length}.
                  </p>
                  {(() => {
                    const proposedCount = candidates.filter((c) => c.status === 'proposed' && c.tnved_code).length;
                    const undeterminedCount = candidates.filter((c) => !c.tnved_code && !c.approved_code).length;
                    if (proposedCount === 0 && undeterminedCount === 0) return null;
                    return (
                      <button
                        onClick={() => void onAcceptAllAndOptimize()}
                        disabled={classifyBusy || proposedCount === 0 || undeterminedCount > 0}
                        title={undeterminedCount > 0 ? `${undeterminedCount} строк без кода — введите вручную` : ''}
                        className="rounded-md bg-ntr-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-ntr-primary/90 disabled:opacity-50"
                      >
                        {classifyBusy
                          ? 'Принимаю…'
                          : `Принять все (${proposedCount}) и оптимизировать →`}
                      </button>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* L3 — Duty calculation */}
          <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-ntr-primary/10">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ntr-primary">
                Расчёт пошлин, НДС и сборов
              </h3>
              <div className="flex items-center gap-2">
                <label className="text-xs text-ntr-muted">Страна:</label>
                <select
                  value={importCountry}
                  onChange={(e) => setImportCountry(e.target.value as typeof importCountry)}
                  className="rounded border border-ntr-primary/20 bg-white px-2 py-1 text-sm"
                >
                  {(['RUS', 'BLR', 'KAZ', 'KGZ', 'ARM'] as const).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => void runCalculateDuty()}
                  disabled={dutyBusy || candidates.filter((c) => c.status === 'approved' || c.status === 'overridden').length === 0}
                  className="rounded-md bg-ntr-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-ntr-primary/90 disabled:opacity-50"
                >
                  {dutyBusy ? 'Расчёт…' : 'Рассчитать пошлины'}
                </button>
              </div>
            </div>
            {dutyMsg && (
              <p className="mt-2 text-sm text-ntr-muted">{dutyMsg}</p>
            )}
            {candidates.filter((c) => c.status === 'approved' || c.status === 'overridden').length === 0 ? (
              <p className="mt-3 text-sm text-ntr-muted">
                Сначала утвердите коды ТН ВЭД в блоке выше.
              </p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-ntr-primary/10 text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-ntr-muted">
                      <th className="px-2 py-2">#</th>
                      <th className="px-2 py-2">Товар</th>
                      <th className="px-2 py-2">Код</th>
                      <th className="px-2 py-2">Там. ст-сть USD</th>
                      <th className="px-2 py-2">RUB</th>
                      <th className="px-2 py-2">Пошлина</th>
                      <th className="px-2 py-2">НДС</th>
                      <th className="px-2 py-2">Сбор</th>
                      <th className="px-2 py-2">Итого</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ntr-primary/10">
                    {candidates
                      .filter((c) => c.status === 'approved' || c.status === 'overridden')
                      .map((c) => {
                        const code = c.approved_code ?? c.tnved_code;
                        const calc = dutyRows.find((d) => d.candidate_id === c.id);
                        return (
                          <tr key={c.id} className="align-top">
                            <td className="px-2 py-2 text-xs text-ntr-muted">{c.row_idx + 1}</td>
                            <td className="px-2 py-2">{c.name ?? '—'}</td>
                            <td className="px-2 py-2 font-mono">{code ?? '—'}</td>
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={customsValues[c.id] ?? (calc ? calc.customs_value : '')}
                                onChange={(e) =>
                                  setCustomsValues({ ...customsValues, [c.id]: e.target.value })
                                }
                                className="w-24 rounded border border-ntr-primary/20 px-2 py-1 text-sm"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-2 py-2 text-xs">
                              {calc ? Number(calc.customs_value_rub).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) : '—'}
                            </td>
                            <td className="px-2 py-2 text-xs">
                              {calc ? Number(calc.duty_rub).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) : '—'}
                            </td>
                            <td className="px-2 py-2 text-xs">
                              {calc ? (
                                <>
                                  {Number(calc.vat_rub).toLocaleString('ru-RU', { maximumFractionDigits: 0 })}
                                  <span className="ml-1 text-ntr-muted">({(Number(calc.vat_rate) * 100).toFixed(0)}%)</span>
                                </>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="px-2 py-2 text-xs">
                              {calc ? Number(calc.customs_fee_rub).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) : '—'}
                            </td>
                            <td className="px-2 py-2 font-semibold">
                              {calc ? Number(calc.total_rub).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                  {dutyAgg && (
                    <tfoot>
                      <tr className="bg-ntr-primary/5 font-semibold">
                        <td className="px-2 py-3" colSpan={4}>
                          Итого по сделке (RUB):
                        </td>
                        <td className="px-2 py-3">
                          {dutyAgg.customs_value_rub.toLocaleString('ru-RU', { maximumFractionDigits: 0 })}
                        </td>
                        <td className="px-2 py-3">
                          {dutyAgg.duty_rub.toLocaleString('ru-RU', { maximumFractionDigits: 0 })}
                        </td>
                        <td className="px-2 py-3">
                          {dutyAgg.vat_rub.toLocaleString('ru-RU', { maximumFractionDigits: 0 })}
                        </td>
                        <td className="px-2 py-3">
                          {dutyAgg.customs_fee_rub.toLocaleString('ru-RU', { maximumFractionDigits: 0 })}
                        </td>
                        <td className="px-2 py-3 text-base">
                          {dutyAgg.total_rub.toLocaleString('ru-RU', { maximumFractionDigits: 0 })}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
                <p className="mt-3 text-xs text-ntr-muted">
                  Ставка пошлины: rds.ett_eaeu_rates (13289 кодов, Решение 80/2021).
                  НДС РФ = 22% (ФЗ-425, 2025-01-01). Курс ЦБ РФ из rds.cbr_rates.
                  Сбор — ПП РФ ‖1 (2022). Используется продакшн-расчёт @ntr/duty-calc.
                </p>
              </div>
            )}
          </div>

          {/* L3 Optimizer */}
          <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-ntr-primary/10">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ntr-primary">
                Оптимизация по кодам ТН ВЭД
              </h3>
              <button
                onClick={() => void runOptimize()}
                disabled={optBusy || candidates.filter((c) => c.status === 'approved' || c.status === 'overridden').length === 0}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                {optBusy ? 'Подбор…' : 'Подобрать выгодный код'}
              </button>
            </div>
            {optMsg && <p className="mt-2 text-sm text-ntr-muted">{optMsg}</p>}
            {!optRun ? (
              <p className="mt-3 text-sm text-ntr-muted">
                Сначала укажите таможенную стоимость в блоке расчёта. Затем оптимизатор сравнит все BERT/CN-handbook кандидаты по реальным ставкам ETT.
              </p>
            ) : (
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-6 rounded-md bg-emerald-50 px-4 py-3 text-sm">
                  <div><span className="text-ntr-muted">Было:</span> <span className="font-mono">{Math.round(Number(optRun.total_naive_rub)).toLocaleString('ru-RU')} RUB</span></div>
                  <div><span className="text-ntr-muted">Станет:</span> <span className="font-mono font-semibold">{Math.round(Number(optRun.total_optimal_rub)).toLocaleString('ru-RU')} RUB</span></div>
                  <div className="text-emerald-700 font-semibold">
                    Экономия {Math.round(Number(optRun.savings_rub)).toLocaleString('ru-RU')} RUB ({(Number(optRun.savings_ratio) * 100).toFixed(1)}%)
                  </div>
                </div>
                <table className="min-w-full divide-y divide-ntr-primary/10 text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-ntr-muted">
                      <th className="px-2 py-2">Товар</th>
                      <th className="px-2 py-2">Текущий код</th>
                      <th className="px-2 py-2">Рекоменд.</th>
                      <th className="px-2 py-2">Экономия</th>
                      <th className="px-2 py-2">Альтернативы</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ntr-primary/10">
                    {optRun.recommendations.map((r) => (
                      <tr key={r.candidate_id} className="align-top">
                        <td className="px-2 py-2">{r.product_name ?? '—'}</td>
                        <td className="px-2 py-2 font-mono text-xs">
                          {r.current_code}
                          <div className="text-ntr-muted">{r.current_cost ? Math.round(r.current_cost).toLocaleString('ru-RU') : '—'}</div>
                        </td>
                        <td className="px-2 py-2 font-mono text-xs">
                          <span className={r.recommended_code !== r.current_code ? 'font-semibold text-emerald-700' : ''}>
                            {r.recommended_code}
                          </span>
                          <div className="text-ntr-muted">{r.recommended_cost ? Math.round(r.recommended_cost).toLocaleString('ru-RU') : '—'}</div>
                        </td>
                        <td className="px-2 py-2">
                          {r.delta_rub > 1 ? (
                            <span className="font-semibold text-emerald-700">−{Math.round(r.delta_rub).toLocaleString('ru-RU')}</span>
                          ) : (
                            <span className="text-ntr-muted">—</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-xs">
                          {r.variants.slice(0, 4).map((v) => (
                            <div key={v.hsCode} className="font-mono flex gap-2">
                              <span className={v.plausible ? '' : 'text-ntr-muted line-through'}>{v.hsCode}</span>
                              <span className="text-ntr-muted">{v.cost ? Math.round(v.cost).toLocaleString('ru-RU') : 'no-rate'}</span>
                            </div>
                          ))}
                          {r.warnings.length > 0 && (
                            <div className="mt-1 text-amber-700">⚠ {r.warnings[0]}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-ntr-muted">
                  Альгоритм: по каждой строке выбирается код с минимальными платежами среди BERT/CN-handbook top-5 (порог плаузибельности 50% от top-1). Ставки живые из rds.ett_eaeu_rates.
                </p>
              </div>
            )}
          </div>

          {dealId && (
            <DealNonTariffSection
              dealId={dealId}
              token={token}
              candidates={candidates.map<CandidateLite>((c) => ({
                id: c.id,
                tnved_code: c.tnved_code,
                approved_code: c.approved_code,
                status: c.status,
                name_ru: c.name_ru,
                name: c.name,
              }))}
            />
          )}

          {dealId && <DealDtDatasetSection dealId={dealId} token={token} />}
        </>
      )}
    </div>
  );
}
